import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Checkbox from '@mui/material/Checkbox';
import Button from '../BaseComponents/Button';
import Loader from '../BaseComponents/Loader';
import { downloadPreScanQuestionnairePdf, getMemberMedicalCard } from '../../api/members';
import { RootState } from '../../store/store';
import { setMemberMedicalCard } from '../../store/memberSlice';
import { formatUKDate } from '../../utils';
import {
  displayQuestionnaireValue,
  formatMemberGender,
  getPreScanQuestionnaireDisclaimerItems,
  isDisclaimerItemConfirmed,
  PRE_SCAN_QUESTIONNAIRE_SECTIONS,
} from '../../utils/preScanQuestionnaireDisplay';
import './ScanQuestionnaires.scss';

type QuestionnaireRow = {
  label: string;
  value: string;
};

const QuestionnaireTable: React.FC<{
  columnLabels: [string, string];
  rows: QuestionnaireRow[];
}> = ({ columnLabels, rows }) => (
  <div className="questionnaire-table">
    <div className="questionnaire-row questionnaire-head">
      <div>{columnLabels[0]}</div>
      <div>{columnLabels[1]}</div>
    </div>
    {rows.map((row) => (
      <div key={row.label} className="questionnaire-row">
        <div>{row.label}</div>
        <div className="questionnaire-answer">{row.value}</div>
      </div>
    ))}
  </div>
);

const ScanQuestionnairesToolbar: React.FC<{
  showDownload?: boolean;
  isDownloading?: boolean;
  onDownload?: () => void;
}> = ({ showDownload, isDownloading, onDownload }) => (
  <div className="scan-questionnaires-toolbar sideBarPad2">
    <div className="scan-questionnaires-count">Pre-scan questionnaires</div>
    {showDownload && onDownload && (
      <div className="scan-questionnaires-toolbar-actions">
        <Button
          text="Download questionnaire"
          onClick={onDownload}
          isSecondary={true}
          variant="download"
          isLoading={isDownloading}
          isDisabled={isDownloading}
        />
      </div>
    )}
  </div>
);

const ScanQuestionnairesEmptyTable: React.FC<{ message: string }> = ({ message }) => (
  <div className="scan-questionnaires-table">
    <div className="scan-questionnaires-table-header">
      <div className="header-cell">Question</div>
      <div className="header-cell">Answer</div>
    </div>
    <div className="scan-questionnaires-table-body">
      <div className="empty-state">
        <div className="empty-text">{message}</div>
      </div>
    </div>
  </div>
);

const ScanQuestionnaires: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const memberId = location.pathname.split('/')[2];
  const { memberData, medicalCard } = useSelector((state: RootState) => state.member);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(true);

  const scanInfo = medicalCard?.scan_info;
  const isCompleted = Boolean(scanInfo?.pre_scan_questionnaire_completed_at);

  useEffect(() => {
    if (!memberId) {
      setIsRefreshing(false);
      return;
    }

    let cancelled = false;

    const refreshMedicalCard = async () => {
      try {
        const card = await getMemberMedicalCard(memberId);
        if (!cancelled) {
          dispatch(setMemberMedicalCard(card));
        }
      } catch {
        // Keep existing Redux data if refresh fails.
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
        }
      }
    };

    refreshMedicalCard();

    return () => {
      cancelled = true;
    };
  }, [memberId, dispatch]);

  const handleDownload = async () => {
    if (!memberId) return;
    setIsDownloading(true);
    try {
      const memberName = `${memberData?.first_name || ''} ${memberData?.last_name || ''}`.trim();
      await downloadPreScanQuestionnairePdf(memberId, {
        memberName,
        submittedAt: scanInfo?.pre_scan_questionnaire_completed_at,
      });
      toast.success('Questionnaire downloaded successfully');
    } catch (error: any) {
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to download questionnaire';
      toast.error(message);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!memberData || isRefreshing) {
    return (
      <div className="scan-questionnaires-container">
        <Loader />
      </div>
    );
  }

  const membershipType = memberData.membership_type?.toLowerCase();
  const isPlusOrScanMember = membershipType === 'plus' || membershipType === 'scan';

  if (!isPlusOrScanMember) {
    return (
      <div className="scan-questionnaires-container">
        <ScanQuestionnairesToolbar />
        <ScanQuestionnairesEmptyTable message="Pre-scan questionnaires are only available for Plus and Scan members." />
      </div>
    );
  }

  if (!isCompleted) {
    return (
      <div className="scan-questionnaires-container">
        <ScanQuestionnairesToolbar />
        <ScanQuestionnairesEmptyTable message="No pre-scan questionnaire has been submitted yet." />
      </div>
    );
  }

  const memberName = `${memberData.first_name || ''} ${memberData.last_name || ''}`.trim();
  const personalInfoRows: QuestionnaireRow[] = [
    { label: 'Full name', value: memberName || '-' },
    { label: 'Date of Birth', value: formatUKDate(memberData.dob, 'dd MMM yyyy') },
    { label: 'Gender', value: formatMemberGender(memberData.gender) },
    { label: 'Phone number', value: memberData.phone || '-' },
    { label: 'Email', value: memberData.email || '-' },
    { label: 'Postal address', value: displayQuestionnaireValue(scanInfo, 'center_address') },
    { label: 'Postcode', value: displayQuestionnaireValue(scanInfo, 'postcode') },
  ];

  return (
    <div className="scan-questionnaires-container">
      <ScanQuestionnairesToolbar
        showDownload
        isDownloading={isDownloading}
        onDownload={handleDownload}
      />

      <div className="scan-questionnaires-content">
        <div className="questionnaire-sections">
          <div className="questionnaire-section">
            <div className="questionnaire-section-header">
              <h3>Personal Information</h3>
            </div>
            <QuestionnaireTable
              columnLabels={['Field', 'Response']}
              rows={personalInfoRows}
            />
          </div>

          {PRE_SCAN_QUESTIONNAIRE_SECTIONS.map((section) => (
            <div key={section.title} className="questionnaire-section">
              <div className="questionnaire-section-header">
                <h3>{section.title}</h3>
              </div>
              <QuestionnaireTable
                columnLabels={['Question', 'Answer']}
                rows={section.fields.map((field) => ({
                  label: field.label,
                  value: displayQuestionnaireValue(scanInfo, field.key),
                }))}
              />
            </div>
          ))}

          <div className="questionnaire-section">
            <div className="questionnaire-section-header">
              <h3>Confirmation</h3>
            </div>
            <div className="questionnaire-disclaimer-checkboxes">
              {getPreScanQuestionnaireDisclaimerItems(memberData.gender).map((item, index) => (
                <div key={item} className="questionnaire-disclaimer-checkbox">
                  <Checkbox
                    id={`questionnaire-disclaimer-${index}`}
                    checked={isDisclaimerItemConfirmed(scanInfo, item)}
                    disabled
                    size="small"
                  />
                  <label htmlFor={`questionnaire-disclaimer-${index}`}>{item}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanQuestionnaires;
